import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip, Warning } from '../../components/LessonUI';

const JSArrowFunctions = () => {
    const syntaxVariationsCode = `// 1. Basic Arrow Syntax
const add = (a, b) => {
    return a + b;
};

// 2. Concise Form (Implicit Return)
const multiply = (a, b) => a * b;

// 3. Single Parameter (No Parentheses)
const square = x => x * x;

// 4. Returning an Object (Parentheses required)
const getUser = id => ({ id, role: 'admin' });`;

    const lexicalThisCode = `// The "This" Problem: Traditional vs. Arrow
const server = {
    status: 'online',
    // Traditional method
    start() {
        console.log(this.status); // 'online'
        
        // Callback loses 'this' context
        setTimeout(function() {
            console.log(this.status); // undefined (or error in strict)
        }, 100);

        // Arrow function inherits 'this' lexically
        setTimeout(() => {
            console.log(this.status); // 'online'
        }, 200);
    }
};

server.start();`;

    const anonymousCode = `// Stack Trace Implication
const processData = (data) => {
    // This is an ANONYMOUS function assigned to a variable
    throw new Error('Debug Trace');
};

// In debugger: stack trace might show "anonymous" 
// unless the engine infers the name from the variable.`;

    const architecturalCode = `// Architectural Use: Currying and HOCs
const withLogger = (prefix) => (fn) => (...args) => {
    console.log(\`[\${prefix}] Calling function with:\`, args);
    return fn(...args);
};

const loggedAdd = withLogger('APP')(add);
loggedAdd(5, 10); // [APP] Calling function with: [5, 10]`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Modern ES6+ Syntax</div>
                <h1>Arrow Functions & This Binding</h1>
                <p className="hero-lead">
                    Arrow functions are more than just syntactic sugar. They fundamentally
                    change how JavaScript handles <strong>contextual binding</strong>, solving
                    one of the most persistent pain points in the language's history.
                </p>
            </div>

            <div className="content-section">
                <h2>Syntax & Mechanics</h2>
                <p>
                    Arrow functions introduced a concise way to write function expressions.
                    Beyond brevity, they allow for <strong>Implicit Returns</strong>a feature
                    that enables incredibly clean functional pipelines.
                </p>
                <CodeBlock language="javascript" code={syntaxVariationsCode} />
                <ProTip><strong> Pro Tip:</strong> Use parentheses <code>()</code> when returning object
                    literals concisely. Without them, the engine interprets the curly braces
                    <code>{`{}`}</code> as a function block, leading to <code>undefined</code> returns
                    or syntax errors.</ProTip>
            </div>

            <div className="content-section">
                <h2>Lexical Scoping: The "This" Gamechanger</h2>
                <p>
                    In traditional functions, the <code>this</code> keyword is <strong>dynamically scoped</strong>it
                    depends on <em>how</em> the function was called. Arrow functions use
                    <strong>Lexical Scoping</strong>, meaning they inherit <code>this</code> from the
                    enclosing execution context where they were defined.
                </p>
                <CodeBlock language="javascript" code={lexicalThisCode} />
                <div className="key-point">
                    Building a mental model: An arrow function doesn't *have* a <code>this</code> of its
                    own. It simply looks up the <strong>Scope Chain</strong> to find the nearest
                    non-arrow function context.
                </div>
            </div>

            <div className="content-section">
                <h2>Memory & Debugging Tradeoffs</h2>
                <p>
                    While powerful, arrow functions come with engineering tradeoffs. Because they
                    are technically <strong>anonymous</strong>, they can sometimes make stack
                    traces harder to read in older environments (though modern V8 engines are
                    excellent at "Inferred Naming").
                </p>
                <CodeBlock language="javascript" code={anonymousCode} />
                <Warning><strong> Caution:</strong> Arrow functions cannot be used as <strong>Constructors</strong>.
                    Attempting to call an arrow function with <code>new</code> will throw a
                    <code>TypeError</code> because they lack a <code>[[Construct]]</code> internal method
                    and a <code>prototype</code> property.</Warning>
            </div>

            <div className="content-section">
                <h2>Architectural Utility</h2>
                <p>
                    Arrow functions excel in <strong>Higher-Order Functions (HOFs)</strong> and
                    <strong>Currying</strong>. They allow developers to create "function factories"
                    with minimal boilerplate, which is the standard pattern in modern React
                    middleware and utility libraries.
                </p>
                <CodeBlock language="javascript" code={architecturalCode} />
            </div>

            <KeyFacts><ul>
                    <li><strong>Use for Callbacks:</strong> Ideal for <code>map</code>, <code>filter</code>, and <code>setTimeout</code>.</li>
                    <li><strong>Use in React Methods:</strong> Prevents "lost context" without manual <code>.bind(this)</code>.</li>
                    <li> <strong>Avoid for Object Methods:</strong> Use shorthand <code>{"method() "}{"{"}{"}"}</code> instead to keep dynamic <code>this</code>.</li>
                    <li> <strong>Avoid for Event Listeners:</strong> If you need <code>this</code> to refer to the DOM element (the current target).</li>
                    <li><strong>Prefer Conciseness:</strong> Use implicit returns for single-expression logic.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSArrowFunctions;



